import { containEvent, debounce, pjaxFetch, fadeIn } from './utilities';
import { activate } from './activate';
import popupOnClick from './popups';

class CaptiveForm {
  constructor(el, opts={}) {
    this.form = el;
    this.options = Object.assign({
      fast: false,
      history: false,
    }, opts);
    this.historical = this.options.history || this.form.dataset.historical;
    this.selector = this.options.into || this.form.dataset.target;
    this.container = document.querySelector(this.selector);
    this.original_qs = this.serialize();
    this.original_content = this.container.innerHTML;
    this.request = null;
    this.inactive = false;
    this.cache = {};
    this.submitSoon = debounce(this.submitForm.bind(this), 500);

    // observe elements
    this.bindLinks();
    if (this.options.fast) {
      this.bindInputs(this.options.fast);
    }
    if (this.historical) {
      this.saveState(this.original_content);
      window.addEventListener("popstate", this.restoreState.bind(this));
    }
  }

  /* CHANGE HANDLERS */

  bindLinks() {
    // console.log("👇 bindLinks", this.form);
    this.form.querySelectorAll('a.cancel').forEach((el) => el.addEventListener('click', this.revert.bind(this)));
    this.form.querySelectorAll('a.param').forEach((el) => el.addEventListener('click', this.setParam.bind(this)));
    this.container.querySelectorAll('.pagination a').forEach((el) => el.addEventListener('click', this.setPage.bind(this)));
  }

  bindInputs(selector) {
    if (typeof selector === "string") {
      // only some fields should be made fast
      this.form.querySelectorAll(selector).forEach((el) => el.addEventListener('change', this.changed.bind(this)));
    } else {
      // react to everything all the time. Only advisable for direct-search forms
      this.form.querySelectorAll('textarea, input[type="search"], input[type="text"]').forEach((el) => el.addEventListener('keyup', this.keyed.bind(this)));
      this.form.querySelectorAll('textarea, input[type="search"], input[type="text"]').forEach((el) => el.addEventListener('change', this.changed.bind(this)));
      this.form.querySelectorAll('input[type="radio"], input[type="checkbox"]').forEach((el) => el.addEventListener('click', this.clicked.bind(this)));
      this.form.querySelectorAll('select').forEach((el) => el.addEventListener('change', this.changed.bind(this)));
    }
  }

  keyed(e) {
    if (e) {
      const k = e.which;
      if (k === 13) {
        this.submitForm(e);
      } else if (this.options.fast && (k >= 46 && k <= 90) || (k >= 96 && k <= 111) || k === 8) {
        this.changed(e);
      }
    }
  }

  changed(e) {
    if (!this.inactive) this.submitSoon();
  }

  clicked(e) {
    if (!this.inactive) this.submitSoon();
  }

  /* FORM HANDLERS */

  serialize() {
    const formData = new FormData(this.form);
    return new URLSearchParams(formData).toString();
  }

  deserialize(qs) {
    const usp = new URLSearchParams(formData).toString();
    usp.forEach((v, k) => {
      this.form.querySelectorAll(`input[name="${k}"]`).foreach((input) => {
        if (input.type === 'radio' || input.type === 'checkbox') {
          input.checked = input.value === v;
        } else {
          input.value = v;
        }
      });
    });
  }

  setPage(e) {
    containEvent(e);
    const url = new URL(e.target.href);
    const p = new URLSearchParams(url.search).get('page');
    // console.log("setPage", e.target.href, p);
    const page_input = this.form.querySelector('input[name="page"]');
    if (page_input) {
      page_input.value = p;
      this.submitSoon();
    }
  }

  setParam(e) {
    containEvent(e);
    const a = e.currentTarget;
    if (a) {
      const param = a.dataset.param;
      const value = a.dataset.value;
      const input = this.form.querySelector(`input[name="${param}"]`)
      // console.log("👇 setParam", a, param, value, input);
      if (input) {
        input.value = value;
        this.submitSoon();
      }
    }
  }

  submitForm(e, nocache) {
    containEvent(e);
    // const qs = this.serialize();
    // if (!nocache && this.cache[qs]) {
    //   this.displayResults(this.cache[qs]);
    // } else {
    //   this.sendRequest(qs);
    // }
    this.form.submit();
  }

  sendRequest(qs) {
    this.form.classList.add('working');
    const method = this.form.method || 'get';
    const url = new URL(this.form.action);
    url.search = qs;
    pjaxFetch(url, {
      method: method
    }).then((text) => this.receiveResults(qs, text));
  }

  receiveResults(qs, content) {
    this.form.classList.remove('working');
    this.cache[qs] = content;
    this.displayResults(content);
    if (this.historical) this.saveState(content, qs);
  }

  displayResults(content) {
    this.container.innerHTML = content;
    this.enableHelpers();
    activate(this.container);
    fadeIn(this.container);
    this.bindLinks();
    window.scroll({top: 0, left: 0, behavior: 'smooth'});
  }

  enableHelpers() {
    this.container.querySelectorAll('a[data-action="popup"]').forEach((el) => {
      popupOnClick(el);
    });
  }

  revert() {
    this.displayResults(this.original_content);
  }

  /* HISTORY HANDLERS */

  saveState(content, qs) {
    const url = new URL(window.location.href);
    url.search = qs || this.serialize();
    const title = `${document.title} search`;
    const state = {
      html: content,
      qs: qs,
    }
    history.pushState(state, title, url);
  }

  restoreState(e) {
    containEvent(e);
    if (e.state && e.state.html) {
      this.displayResults(e.state.html);
      this.deserialize(e.state.qs);
    }
  }

}


function captiveForm(el) {
  return new CaptiveForm(el);
}

function facetingSearch(el, opts={}) {
  const options = Object.assign({
    fast: true,
    history: true,
  }, opts);
  return new CaptiveForm(el, options);
}

export {
  captiveForm,
  facetingSearch,
} 
